import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, Injector, Input, OnInit, ElementRef, HostListener,
  signal,
  computed,
  input,
  ViewChild,
  ViewChildren,
  QueryList,
  OnDestroy,
  effect,
  ChangeDetectorRef
} from '@angular/core';
import { startWith, Subject, takeUntil } from 'rxjs';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BaseControlValueAccessor, SharedFormPipesModule } from '@sondermind/iris/form-helpers';
import { IconColors, IconSizes, IconUsageTypes, irisIcon, IrisIconComponent } from '@sondermind/iris/icon';
import { DropdownOption, NavigationKeys } from '@sondermind/iris/dropdown';
import { IrisButtonContainerAlignments, IrisButtonModule, IrisButtonTypes } from '@sondermind/iris/button';
import { IrisIconButtonModule, IrisIconButtonTypes } from '@sondermind/iris/icon-button';
import { IrisButtonTypeAttributeOptions } from '@sondermind/iris/shared';
import { IOptionCategory } from '../../models/option-category.interface';
import { filterNonEmptyOptionCategories } from '../../utils/option-search.util';
import { setsAreEqual, valuesAreEqual } from '../../utils/value-equality.util';

@Component({
  selector: 'filter-select',
  templateUrl: './filter-select.component.html',
  styleUrls: ['./filter-select.component.scss', '../../styles/select-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedFormPipesModule,
    MatCheckboxModule,
    IrisIconComponent,
    IrisButtonModule,
    IrisIconButtonModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FilterSelectComponent,
      multi: true
    }
  ]
})
export class FilterSelectComponent<T> extends BaseControlValueAccessor implements OnInit, OnDestroy {
  constructor(
    injector: Injector,
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    super(injector);

    // Effect to clear search query when search mode changes
    effect(() => {
      const isSearching = this.isSearching();
      if (!isSearching) {
        this.searchQuery.set('');
      }
    });
  }

  @ViewChild('input', { static: true }) inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('clearButton', { read: ElementRef }) clearButton!: ElementRef<HTMLElement>;
  @ViewChildren('listItem') listItems!: QueryList<ElementRef<HTMLLIElement>>;

  @Input() placeholder: string = '';
  @Input() emptyOptionText: string = '';
  /** Id of an external visible `<label>`; suppresses `aria-label`, which would otherwise outrank it. */
  @Input() ariaLabelledBy: string = '';
  readonly categorizedOptions = input<Array<IOptionCategory<T>>>([]);
  @Input() isMulti: boolean = false;
  @Input() isSearchable: boolean = true;
  /** When true (and searchable), the input keeps a consistent min-width so the filter does not grow when expanded. */
  @Input() maintainFilterWidth: boolean = false;
  /** Anchors the option panel to the right edge of the field instead of the left. */
  @Input() alignRight: boolean = false;
  /** Sizes the option panel to the field instead of to its longest option. */
  @Input() matchFieldWidth: boolean = false;
  @Input() size: number = 8;
  /** Chevron glyph shown while the option panel is collapsed. */
  @Input() expandIcon: irisIcon = 'expand_more';
  /** Chevron glyph shown while the option panel is open. */
  @Input() collapseIcon: irisIcon = 'expand_less';

  @Input() customErrorMessages: { [key: string]: string; } = {};
  @Input() clearButtonType: IrisButtonTypeAttributeOptions =
  IrisButtonTypeAttributeOptions.SUBMIT;
  iconSizes = IconSizes;
  iconUsageTypes = IconUsageTypes;
  iconColors = IconColors;
  irisButtonContainerAlignments = IrisButtonContainerAlignments;
  irisButtonTypes = IrisButtonTypes;
  irisIconButtonTypes = IrisIconButtonTypes;
  irisButtonTypeAttributeOptions = IrisButtonTypeAttributeOptions;

  /** Checks if form control has required validator */
  isRequired: boolean = false;

  /** Handles expanded/collapsed state of menu */
  isExpanded: boolean = false;

  /** Display name for UI */
  readonly selectionDisplayName = signal<string | null>(null);

  /** Selected options for multi-select mode */
  private selectedValues = new Set<T>();

  /** Temporary selected options for multi-select mode (before applying) */
  temporarySelectedValues = new Set<T>();

  /** Track currently focused option for keyboard navigation */
  currentFocusedIndex: number = -1;

  /** Whether we're currently searching (input has focus and dropdown is open) */
  readonly isSearching = signal<boolean>(false);

  /** Search term for filtering options */
  readonly searchQuery = signal<string>('');

  /** Signal for control value length to trigger change detection */
  readonly controlValueLength = signal<number>(0);

  /** Results from search */
  readonly filteredOptions = computed(() => {
    const options = this.categorizedOptions();
    return this.isSearchable ? filterNonEmptyOptionCategories(options, this.searchQuery()) : options;
  });

  // Signal for last visible category index
  readonly lastVisibleCategoryIndex = computed<number>(() => {
    const categories = this.filteredOptions();
    for (let i = categories.length - 1; i >= 0; i -= 1) {
      if (categories[i].options.length > 0) return i;
    }
    return -1;
  });

  /** Flattened options for keyboard navigation */
  readonly flattenedOptions = computed(() => this.filteredOptions().flatMap((category) => category.options));

  /** Whether to show empty option text */
  readonly shouldShowEmptyOptionText = computed(() => !this.filteredOptions()[0]?.options?.length && !!this.emptyOptionText);

  /** Error messages to display based on control validation errors */
  get errorMessagesToDisplay(): string[] {
    const { errors } = this.control;
    if (!errors || Object.keys(this.customErrorMessages).length === 0) {
      return [];
    }

    const errorMessages: string[] = [];

    Object.keys(errors).forEach((errorKey) => {
      const message = this.customErrorMessages[errorKey];
      if (message) {
        errorMessages.push(message);
      }
    });

    return errorMessages;
  }

  /** Check if error messages should be displayed */
  get shouldShowErrors(): boolean {
    return !!(this.control.touched && this.control.invalid && this.errorMessagesToDisplay.length > 0);
  }

  /** If search yields no results */
  readonly hasNoOptions = computed(() => {
    if (!this.isSearchable) {
      return false;
    }

    const searchTerm = this.searchQuery();
    if (!searchTerm.trim()) {
      return false;
    }

    return this.flattenedOptions().length === 0;
  });

  readonly optionIndexMap = computed(() => {
    const options = this.filteredOptions();
    const indexMap = new Map<DropdownOption<T>, number>();
    let globalIndex = 0;

    options.forEach((category: IOptionCategory<T>) => {
      category.options.forEach((option: DropdownOption<T>) => {
        indexMap.set(option, globalIndex);
        globalIndex += 1;
      });
    });

    return indexMap;
  });

  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.isRequired = this.control.hasValidator(Validators.required);

    // Subscribe to form control value changes to update display name
    this.control.valueChanges.pipe(
      startWith(this.control.value),
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.setDisplayAndSelectedValues();
    });

    this.control.statusChanges.pipe(
      takeUntil(this.destroyed$)
    ).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private setDisplayAndSelectedValues(): void {
    const { value } = this.control;

    if (value === null || value === undefined) {
      this.selectionDisplayName.set(null);
    } else if (this.isMulti && (value as T[])?.length === 0) {
      this.selectionDisplayName.set(null);
    } else if (this.isMulti && Array.isArray(value)) {
      if (value.length === 1) {
        const option = this.flattenedOptions().find((opt: DropdownOption<T>) => valuesAreEqual(opt.value, value[0]));
        this.selectionDisplayName.set(option?.name || null);
      } else {
        this.selectionDisplayName.set(null);
      }
    } else if (!this.isMulti && !!value) {
      const option = this.flattenedOptions().find((opt: DropdownOption<T>) => valuesAreEqual(opt.value, value));
      this.selectionDisplayName.set(option?.name || null);
    }

    if (this.isMulti) {
      this.selectedValues = new Set(value);
    }

    this.controlValueLength.set(Array.isArray(value) ? value.length : 0);
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:focusin', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isExpanded) return;

    const target = event.target as HTMLElement;
    // Close dropdown if click is outside the component
    if (!this.elementRef.nativeElement.contains(target)) {
      this.selectionAndCloseDropdown();
    }
  }

  expandClickMenu(e: MouseEvent): void {
    // Check if the click target is the clear button
    const target = e.target as HTMLElement;
    const isClearButton = target?.closest('.clear-value-icon');

    // If clicking on clear button, don't expand/collapse the menu
    if (isClearButton) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!this.isExpanded) {
      this.isExpanded = true;
      this.initializeValues();
    } else {
      this.selectionAndCloseDropdown();
    }
  }

  initializeValues(): void {
    // Initialize temporary selections for multi-select mode
    if (this.isMulti) {
      this.temporarySelectedValues = new Set(this.selectedValues);
    }

    // Force change detection to update readonly attribute in DOM immediately
    // This ensures mobile keyboards open properly
    this.cdr.detectChanges();

    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
      if (this.selectionDisplayName()) {
        this.inputElement.nativeElement.setSelectionRange(0, 0);
      }
    }

    if (!this.isSearchable) return;

    this.isSearching.set(true);
  }

  handleKeyDown(e: KeyboardEvent): void {
    if (NavigationKeys[e.key as keyof typeof NavigationKeys]) {
      this.handleKeyNavigation(e);
    }
  }

  handleKeyNavigation(e: KeyboardEvent): void {
    switch (e.key) {
      case NavigationKeys.Tab:
        if (this.isExpanded) {
          this.handleTabNavigation(e);
        }
        break;
      case NavigationKeys.Enter:
      case NavigationKeys[' ']:
        if (this.isExpanded) {
          e.preventDefault();
          const focusedOption = this.getFocusedOption();
          if (focusedOption) {
            this.changeSelection(focusedOption, !this.isMulti);
          }
        }
        break;
      case NavigationKeys.Escape:
        e.preventDefault();
        this.escapeMenu(e);
        break;
      case NavigationKeys.Home:
        if (this.isExpanded) {
          e.preventDefault();
          this.focusListItem(0);
        }
        break;
      case NavigationKeys.End:
        if (this.isExpanded) {
          e.preventDefault();
          this.focusListItem(this.flattenedOptions().length - 1);
        }
        break;
      case NavigationKeys.ArrowUp:
        e.preventDefault();
        if (this.isExpanded) {
          this.navigateList(NavigationKeys.ArrowUp);
        }
        break;
      case NavigationKeys.ArrowDown:
        e.preventDefault();
        if (this.isExpanded) {
          this.navigateList(NavigationKeys.ArrowDown);
        }
        break;
      default:
        break;
    }
  }

  escapeMenu(e: Event): void {
    this.selectionAndCloseDropdown();
  }

  onInputFocus(e: FocusEvent): void {
    // Expand dropdown when input receives focus via tab
    if (!this.isExpanded) {
      this.isExpanded = true;
      this.initializeValues();
    }
  }

  onInputChange(e: Event): void {
    if (this.isSearching() && this.isSearchable) {
      const target = e.target as HTMLInputElement;
      this.searchQuery.set(target.value);
    }
  }

  handleTabNavigation(e: KeyboardEvent): void {
    // Shift+Tab must release focus backward to the previous filter rather than
    // trapping it forward onto the Clear button, so close and let native nav run.
    if (e.shiftKey) {
      this.selectionAndCloseDropdown();
      return;
    }
    const clearBtn = this.clearButton?.nativeElement?.querySelector('button') ?? null;
    if (!clearBtn) {
      this.selectionAndCloseDropdown();
    } else {
      e.preventDefault();
      clearBtn.focus();
    }
  }

  changeSelection(option: DropdownOption<T>, closeDropdown: boolean = true): void {
    if (this.isMulti) {
      // Multi-select mode: toggle option using Set for O(1) operations
      if (this.temporarySelectedValues.has(option.value)) {
        // Remove if already selected
        this.temporarySelectedValues.delete(option.value);
      } else {
        // Add if not selected
        this.temporarySelectedValues.add(option.value);
      }
    } else {
      // Single-select mode: set single value
      this.control.setValue(option.value);
    }

    // Close dropdown if requested
    if (closeDropdown) {
      this.selectionAndCloseDropdown();
    }
  }

  clearTemporarySelections(): void {
    this.temporarySelectedValues.clear();
  }

  onOptionClick(option: DropdownOption<T>, event: MouseEvent | any): void {
    if (event?.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    // In multi-select mode, don't close the dropdown so users can select multiple options
    // and then choose Clear or Done
    this.changeSelection(option, !this.isMulti);

    if (this.isMulti) {
      this.focusSearchInput();
    }
  }

  private updateInputValue(): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.value = this.selectionDisplayName() || '';
    }
  }

  private focusSearchInput(): void {
    if (this.inputElement) {
      setTimeout(() => {
        this.inputElement?.nativeElement?.focus();
      }, 0);
    }
  }

  selectionAndCloseDropdown(): void {
    // Only write back when the selection actually changed. An unconditional setValue on every
    // dismiss re-emits the form, which re-navigates the router and scrolls the page to the top.
    if (this.isMulti && !setsAreEqual(this.temporarySelectedValues, this.selectedValues)) {
      this.control.setValue(Array.from(this.temporarySelectedValues));
    }
    this.isExpanded = false;
    this.isSearching.set(false);
    this.updateInputValue();
    this.currentFocusedIndex = -1;
  }

  private navigateList(direction: string): void {
    // Navigate through options
    const options = this.flattenedOptions();
    if (options.length === 0) return;

    if (this.currentFocusedIndex === -1) {
      this.currentFocusedIndex = direction === NavigationKeys.ArrowUp ? options.length - 1 : 0;
    } else if (direction === NavigationKeys.ArrowUp) {
      this.currentFocusedIndex = this.currentFocusedIndex > 0 ? this.currentFocusedIndex - 1 : options.length - 1;
    } else {
      this.currentFocusedIndex = this.currentFocusedIndex < options.length - 1 ? this.currentFocusedIndex + 1 : 0;
    }
    this.focusListItem(this.currentFocusedIndex);
  }

  private focusListItem(index: number): void {
    const options = this.flattenedOptions();
    if (index >= 0 && index < options.length) {
      this.currentFocusedIndex = index;
      const listItemElements = this.listItems.toArray();
      if (listItemElements[index]) {
        listItemElements[index].nativeElement.focus();
      }
    }
  }

  private getFocusedOption(): DropdownOption<T> | null {
    const options = this.flattenedOptions();
    if (this.currentFocusedIndex >= 0 && this.currentFocusedIndex < options.length) {
      return options[this.currentFocusedIndex];
    }
    return null;
  }

  clearSelection(event: Event): void {
    this.control.setValue(this.isMulti ? [] : null);
  }
}
